import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Alert, Box, Button, Chip, CircularProgress, Grid, Paper, Stack, TextField } from "@mui/material";
import axios from "../../axiosConfig";
import { buildDefaultValues, buildUserData, isInputField } from "../../utils/dynamicForm";
import { useAttachmentRules } from "../../hooks/useAttachmentRules";
import { InputField, StaticField } from "../RequestServiceForm/DynamicFormComponent";
import AttachmentUploadRows from "../AttachmentUploadRows";
import PreviewAttachmentDialog from "../PreviewAttachmentDialog";

// The citizen's answer to an open staff request (CitizenInfoRequests), shown in the replies thread under the
// staff message. The request is on hold until the citizen answers. Mirrors the server rules in
// CitizenInfoRequestService.
const ADDITIONAL_ATTACHMENTS_TYPE = 129;
const FILE_PREFIX = "infoAttachment";

const parseFields = (schema) => {
  try {
    const fields = JSON.parse(schema || "null");
    return Array.isArray(fields) ? fields : [];
  } catch {
    return [];
  }
};

const InfoRequestCard = ({ applicationId, request, onAnswered }) => {
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const { validFileTypes, fileError } = useAttachmentRules();

  const fields = useMemo(() => parseFields(request?.formSchema), [request?.formSchema]);
  const attachmentItems = useMemo(
    () =>
      (request?.attachmentTypes || []).map((type) => ({
        attachmentTypeId: type.id,
        attachmentType: type.name,
        isMandatory: type.id !== ADDITIONAL_ATTACHMENTS_TYPE,
      })),
    [request?.attachmentTypes]
  );

  const {
    control,
    handleSubmit,
    reset,
    setError: setFieldError,
    clearErrors,
    formState: { errors },
  } = useForm({ mode: "onChange", defaultValues: { dynamic: buildDefaultValues(fields) } });

  useEffect(() => {
    // InputField registers its inputs as dynamic.f<index>.
    reset({ dynamic: buildDefaultValues(fields) });
  }, [fields, reset]);

  if (!request || request.status !== 1) return null;

  const onPick = (field, file, index, name) => {
    if (!file) return;
    const message = fileError(file);
    if (message) {
      setFieldError(name, { type: "validate", message });
      return;
    }
    field.onChange(file);
    clearErrors(name);
  };

  const submit = handleSubmit(async (values) => {
    setError("");
    const chosen = attachmentItems
      .map((item, index) => ({ typeId: item.attachmentTypeId, file: values[`${FILE_PREFIX}${index}`] }))
      .filter((item) => item.file);
    if (!replyText.trim() && chosen.length === 0 && fields.length === 0) {
      setError("الرجاء كتابة رد أو إرفاق ملف.");
      return;
    }

    const body = new FormData();
    body.append("ReplyText", replyText.trim());
    if (fields.length > 0) body.append("FormData", JSON.stringify(buildUserData(fields, values.dynamic)));
    chosen.forEach((item, index) => {
      body.append(`Attachments[${index}].AttachmentType`, item.typeId);
      body.append(`Attachments[${index}].File`, item.file);
    });

    setSending(true);
    try {
      await axios.post(`/Application/${applicationId}/InfoRequest/${request.id}/Reply`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReplyText("");
      onAnswered?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "تعذّر إرسال الرد، حاول مرة أخرى.");
    } finally {
      setSending(false);
    }
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "primary.main" }}>
      <Stack spacing={2}>
        <Box>
          <Chip label="بانتظار ردك" size="small" color="warning" sx={{ fontWeight: 700 }} />
        </Box>

        {attachmentItems.length > 0 && (
          <AttachmentUploadRows
            items={attachmentItems}
            control={control}
            errors={errors}
            namePrefix={FILE_PREFIX}
            accept={validFileTypes}
            onPick={onPick}
            onView={(file) => setPreview({ fileObject: file })}
          />
        )}

        {fields.length > 0 && (
          <Grid container spacing={3}>
            {fields.map((field, index) => (
              <Grid
                item
                xs={12}
                sm={["textarea", "header", "paragraph"].includes(field.type) ? 12 : 6}
                key={`${field.name || field.type}-${index}`}
              >
                {isInputField(field) ? (
                  <InputField field={field} index={index} control={control} errors={errors} />
                ) : field.type === "header" || field.type === "paragraph" ? (
                  <StaticField field={field} />
                ) : null}
              </Grid>
            ))}
          </Grid>
        )}

        <TextField
          label="ردّك"
          multiline
          minRows={3}
          fullWidth
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          inputProps={{ maxLength: 4000 }}
        />

        {error && <Alert severity="error">{error}</Alert>}

        <Box>
          <Button variant="contained" onClick={submit} disabled={sending} sx={{ minWidth: 140 }}>
            {sending ? <CircularProgress size={22} color="inherit" /> : "إرسال الرد"}
          </Button>
        </Box>
      </Stack>

      {preview && (
        <PreviewAttachmentDialog open attachmentDetails={preview} handleClose={() => setPreview(null)} />
      )}
    </Paper>
  );
};

export default InfoRequestCard;
